import { api } from './api';

export interface OrdenLinea {
  id: number;
  ordenId: number;
  productoId: number;
  varianteId?: number;
  nombreProducto: string;
  cantidad: number;
  precioUnitario: number;
  descuento: number;
  subtotal: number;
  notas?: string;
  producto?: {
    id: number;
    name: string;
    sku?: string;
  };
  variante?: {
    id: number;
    name: string;
  };
}

export interface Pago {
  id: number;
  ordenId: number;
  tipoPago: 'efectivo' | 'tarjeta' | 'transferencia' | 'vale' | 'otros';
  monto: number;
  referencia?: string;
  notas?: string;
  createdAt?: string;
}

export interface Orden {
  id: number;
  numeroOrden?: string;
  sesionCajaId: number;
  sucursalId: number;
  usuarioId: number;
  clienteId?: number;
  tipoOrden: 'venta' | 'devolucion' | 'cancelacion';
  subtotal: number;
  impuestos: number;
  descuento: number;
  propina: number;
  total: number;
  estado: 'pendiente' | 'en_proceso' | 'completada' | 'cancelada';
  mesa?: string;
  comensales: number;
  notas?: string;
  sesionCaja?: {
    id: number;
    fechaApertura: string;
  };
  cliente?: {
    id: number;
    nombre: string;
    email?: string;
    telefono?: string;
  };
  sucursal?: {
    id: number;
    name: string;
  };
  lineas?: OrdenLinea[];
  pagos?: Pago[];
  createdAt?: string;
  updatedAt?: string;
}

export interface OrdenCreate {
  sesionCajaId: number;
  sucursalId: number;
  clienteId?: number;
  lineas: Array<{
    productoId: number;
    varianteId?: number;
    cantidad: number;
    precioUnitario?: number;
    descuento?: number;
    notas?: string;
  }>;
  mesa?: string;
  comensales?: number;
  notas?: string;
  impuestos?: number;
  descuento?: number;
}

export interface OrdenPago {
  pagos: Array<{
    tipoPago: 'efectivo' | 'tarjeta' | 'transferencia' | 'vale' | 'otros';
    monto: number;
    referencia?: string;
    notas?: string;
  }>;
}

export interface PaginatedResponse<T> {
  data: {
    [key: string]: T[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

export const ordenService = {
  getAll: async (
    page = 1,
    limit = 20,
    sesionCajaId?: number,
    estado?: string,
    sucursalId?: number,
    fechaDesde?: string,
    fechaHasta?: string
  ): Promise<PaginatedResponse<Orden>> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString()
    });
    if (sesionCajaId) params.append('sesionCajaId', sesionCajaId.toString());
    if (estado) params.append('estado', estado);
    if (sucursalId) params.append('sucursalId', sucursalId.toString());
    if (fechaDesde) params.append('fechaDesde', fechaDesde);
    if (fechaHasta) params.append('fechaHasta', fechaHasta);
    
    const response = await api.get<PaginatedResponse<Orden>>(`/ordenes?${params}`);
    return response.data;
  },

  getById: async (id: number): Promise<{ data: { orden: Orden } }> => {
    const response = await api.get<{ data: { orden: Orden } }>(`/ordenes/${id}`);
    return response.data;
  },

  create: async (data: OrdenCreate): Promise<{ data: { orden: Orden } }> => {
    const response = await api.post<{ data: { orden: Orden } }>('/ordenes', data);
    return response.data;
  },

  procesarPago: async (id: number, data: OrdenPago): Promise<{ data: { orden: Orden } }> => {
    const response = await api.post<{ data: { orden: Orden } }>(`/ordenes/${id}/pago`, data);
    return response.data;
  },

  agregarLineas: async (id: number, data: { lineas: Array<{ productoId: number; varianteId?: number; cantidad: number; precioUnitario?: number; descuento?: number; notas?: string }>; impuestos?: number; descuento?: number }): Promise<{ data: { orden: Orden } }> => {
    const response = await api.put<{ data: { orden: Orden } }>(`/ordenes/${id}/lineas`, data);
    return response.data;
  },

  cancelar: async (id: number, motivo?: string): Promise<{ data: { orden: Orden } }> => {
    const response = await api.post<{ data: { orden: Orden } }>(`/ordenes/${id}/cancelar`, { motivo });
    return response.data;
  }
};

